<script setup lang="ts">
import {computed, ref} from 'vue';
import {Icon} from '@iconify/vue';
import Collapsible from "@/components/generic/Collapsible.vue";
import TranspositionPanel from '@/components/transposition/TranspositionPanel.vue';
import DiatonicShiftPanel from '@/components/transposition/DiatonicShiftPanel.vue';
import VoiceMergePanel from '@/components/transposition/VoiceMergePanel.vue';
import ChordInferencePanel from '@/components/transposition/ChordInferencePanel.vue';
import BassPreviewPanel from '@/components/transposition/BassPreviewPanel.vue';
import {storeToRefs} from "pinia";
import {useMusicUploadStore} from "@/stores/useMusicUploadStore.ts";
import {CHORD_INFERENCE_DISPLAY, formatChordInferenceText} from '@/components/chord-inference';
import {HANDWRITTEN_BASS_COLOR, INFERRED_CHORD_COLOR} from '@/composables/musicXmlDom';

const isOpen = defineModel<boolean>('isOpen', {default: true, type: Boolean});

const {analysisResult, chordInferenceState, bassPreviewState} = storeToRefs(useMusicUploadStore())

// Hinweis-Punkt am Tab „Akkorderkennung" – Farbe und Tooltip aus der zentralen Zustandstabelle.
const chordInferenceTabDot = computed(() => CHORD_INFERENCE_DISPLAY[chordInferenceState.value.state].tabDotColor);
const chordInferenceTabTitle = computed(() => formatChordInferenceText(
    CHORD_INFERENCE_DISPLAY[chordInferenceState.value.state].title,
    chordInferenceState.value.count,
));
type TabName = 'transpose' | 'shift' | 'detect' | 'bass' | 'merge';

type TabType = { name: TabName; label: string; icon: string };
const tabs = [
  {name: 'transpose', label: 'Transposition', icon: 'fa6-solid:arrows-turn-to-dots'},
  {name: 'shift', label: 'Akkorde', icon: 'fa6-solid:layer-group'},
  {name: 'detect', label: 'Akkorderkennung', icon: 'game-icons:guitar-head'},
  {name: 'bass', label: 'Bass', icon: 'game-icons:f-clef'},
  {name: 'merge', label: 'Stimmen', icon: 'fa6-solid:object-group'},
] satisfies ReadonlyArray<TabType>;

// Punkt am Tab „Bass": violett = erzeugt, bernstein = enthält eigene Takte (die beim
// Neuerzeugen verschont bleiben). Kein Bass-System → kein Punkt. Dieselben Farb-Konstanten wie
// die Noten selbst (vorher hartkodiert, das Bernstein war dabei auf einen helleren Ton
// abgedriftet – die Farbsprache soll überall identisch sein).
const bassTabDot = computed(() => ({
  none: '', generated: INFERRED_CHORD_COLOR, edited: HANDWRITTEN_BASS_COLOR,
}[bassPreviewState.value]));
const bassTabTitle = computed(() => ({
  none: '', generated: 'Bass-System erzeugt', edited: 'Bass-System enthält eigene Takte',
}[bassPreviewState.value]));

const tabsAvailable = computed<TabType[]>(() => {
  // 'merge' nur bei mehrstimmigen Stücken; Akkorderkennung funktioniert auch einstimmig.
  const multi = (analysisResult.value?.voices ?? 0) > 1;
  return tabs.filter(t => t.name !== 'merge' || multi);
})
const activeTab = ref<TabName>('transpose');
</script>

<template>
  <!--  <div class="glass-panel p-6 max-w-full">-->
  <!-- Operation Select -->
  <div class="min-w-0 w-full sm:w-auto sm:grow sm:basis-[22rem] lg:w-full lg:basis-auto">
    <Collapsible v-if="activeTab" variant='card' icon="fa6-solid:pen-to-square" title="Transpositionen" v-model:is-open="isOpen">

      <template #default>
        <div>
          <nav
              class="mb-6 grid md:flex md:flex-wrap max-w-full gap-2 rounded-xl border border-gray-200 bg-gray-100 p-1.5"
              aria-label="Transpositions-Tabs"
          >
            <button
                v-for="tab in tabsAvailable"
                :key="tab.name"
                type="button"
                class="flex w-fit flex-auto items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium leading-5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                :class="activeTab === tab.name
            ? 'bg-white text-blue-700 shadow-sm'
            : 'text-gray-500 hover:bg-white/70 hover:text-gray-700'"
                @click="activeTab = tab.name"
            >
              <Icon :icon="tab.icon" class="text-base" :class="activeTab === tab.name ? 'text-blue-600' : 'text-gray-400'"/>
              <span class="truncate">{{ tab.label }}</span>
              <!-- Punkt: Hinweis zur automatischen Akkorderkennung, ohne den Tab zu öffnen.
                   Magenta = Auto-Akkorde vorhanden, Amber = Erkennung lief ohne Treffer. Die
                   ruhigen Zustände (nie analysiert / entfernt) zeigen bewusst keinen Punkt. -->
              <span
                  v-if="tab.name === 'bass' && bassTabDot"
                  class="inline-block w-2 h-2 rounded-full shrink-0"
                  :style="{background: bassTabDot}"
                  :title="bassTabTitle"
              ></span>
              <span
                  v-if="tab.name === 'detect' && chordInferenceTabDot"
                  class="inline-block w-2 h-2 rounded-full shrink-0"
                  :style="{background: chordInferenceTabDot}"
                  :title="chordInferenceTabTitle"
              ></span>
            </button>
          </nav>

          <!-- v-show statt v-if: lokaler Panel-State (z. B. manuelles Intervall) bleibt beim
               Tab-Wechsel erhalten. -->
          <div v-show="activeTab === 'transpose'">
            <TranspositionPanel class="animate-fade-right animate-duration-200"/>

          </div>
          <div v-show="activeTab === 'shift'">
            <DiatonicShiftPanel class="animate-fade-right animate-duration-200"/>
          </div>
          <div v-show="activeTab === 'detect'">
            <ChordInferencePanel class="animate-fade-right animate-duration-200"/>
          </div>
          <div v-show="activeTab === 'bass'">
            <BassPreviewPanel class="animate-fade-right animate-duration-200"/>
          </div>
          <div v-show="activeTab === 'merge'">
            <VoiceMergePanel class="animate-fade-right animate-duration-200"/>
          </div>
        </div>
      </template>
    </Collapsible>

    <!-- Tab-Navigation -->
  </div>
  <!--  </div>-->
</template>
